import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy, HostListener } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalErrorHandlerService } from '@k12/admin-services';
import { TasksService, TaskTypeService } from '@k12/tasks';
import { ToastMessageService } from '@k12/app-services';
import {
  PandaDocCreateSessionRequestDto,
  SubTask,
  Task,
  TaskType,
  TaskTypeAction,
  ToastMessageType
} from '@k12/models';
import { LoadingSpinnerService } from '@k12/loading';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Subject, finalize  } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { MatCard } from '@angular/material/card';
import { TaskHeaderInfoComponent } from '@k12/task-view'

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-task-pandadoc',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    FormsModule,
    MatCard,
    TaskHeaderInfoComponent
  ],
  standalone: true,
  styleUrls: ['./task-pandadoc.component.scss'],
  templateUrl: './task-pandadoc.component.html'
})
export class TaskPandaDocComponent implements OnInit, OnDestroy {
  @HostListener('window:message', ['$event'])
  public handlePandaDocMessage(event: MessageEvent): void {
    if (event.origin !== 'https://app.pandadoc.com') {
      return;
    }

    const messageType = event.data?.type;

    switch (messageType) {
      case 'session_view.document.loaded':
        this.isLoadingSession = false;
        this.cdr.markForCheck();
        break;

      case 'session_view.document.completed':
        this.handlePandaDocCompleted();
        break;

      case 'session_view.document.exception':
        this.toastService.showToast(
          ToastMessageType.Error,
          'An error occurred while loading the PandaDoc document.'
        );

        console.error(
          'PandaDoc signing exception:',
          event.data?.payload
        );
        break;
    }
  }
  private readonly globalErrorHandlerService: GlobalErrorHandlerService = inject(GlobalErrorHandlerService);

  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly taskService: TasksService = inject(TasksService);
  private readonly taskTypeService: TaskTypeService = inject(TaskTypeService);
  private readonly toastService = inject(ToastMessageService);
  private readonly loadingSpinnerService = inject(LoadingSpinnerService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly unsubscribe = new Subject<void>();
  
  public errorMessage: string = '';
  public task: Task | null;
  public taskType: TaskType;
  public taskAction?: SubTask;
  public taskTypeAction?: TaskTypeAction;
  public taskCompleted: boolean = false;
  public recentTaskNote: string = '';
  public pandaDocUrl: string = '';
  public isLoadingSession: boolean = false;
  public documentOpened: boolean = false;

  constructor() {}

  public get canSubmit(): boolean {
    return this.documentOpened && this.subTaskEditable();
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public ngOnInit(): void {
    const taskId = this.route.snapshot.paramMap.get('id');
    const taskActionId = this.route.snapshot.paramMap.get('typeId');

    if (!taskId || !taskActionId) {
      this.toastService.showToast(ToastMessageType.Error, 'Invalid task or task action ID.');
      this.globalErrorHandlerService.handleError({ message: 'Invalid task or task action ID.' });
      return;
    }

    this.loadingSpinnerService.showSpinner();
    this.taskService
      .get(taskId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (task) => {
          this.task = task.body;
          if (!this.task?.subTasks) {
            this.toastService.showToast(ToastMessageType.Error, 'Task has no subtasks.');
            this.globalErrorHandlerService.handleError({ message: 'Task has no subtasks.' });
            this.loadingSpinnerService.hideSpinner();
            return;
          }
          this.taskAction = this.task?.subTasks?.find(
            (subTask) => subTask.id?.toLowerCase() === taskActionId.toLowerCase()
          );
          this.taskCompleted = !this.subTaskEditable();
          if (this.taskCompleted) {
            this.documentOpened = true;
          }
          this.cdr.markForCheck();
          if (!this.taskAction) {
            this.toastService.showToast(ToastMessageType.Error, 'Task action not found.');
            this.globalErrorHandlerService.handleError({ message: 'Task action not found.' });
            this.loadingSpinnerService.hideSpinner();
            return;
          }

          this.taskTypeService
            .get(this.taskAction.taskTypeId)
            .pipe(takeUntil(this.unsubscribe))
            .subscribe({
              next: (taskType) => {
                if (!taskType.body) {
                  return;
                }
                this.taskType = taskType.body;
                this.taskTypeAction = this.taskType.subTasks?.find(
                  (subTask) => subTask.actionId?.toLowerCase() === this.taskAction?.taskTypeSubTaskId.toLowerCase()
                );

                if (this.task && this.taskTypeAction?.$type?.toLowerCase() === 'pandadoc') {
                  // Load task notes
                  this.loadTaskNotes(taskId);

                  // Check if we already have a PandaDoc URL in actionUri
                  if (this.taskTypeAction.pandaDocTemplateId) {
                    this.loadingSpinnerService.hideSpinner();
                  } else {
                    this.loadingSpinnerService.hideSpinner();
                    // Create a new PandaDoc session
                    this.toastService.showToast(ToastMessageType.Error, 'This is not a PandaDoc task template id');
                  }
                } else {
                  this.loadingSpinnerService.hideSpinner();
                  this.toastService.showToast(ToastMessageType.Error, 'This is not a PandaDoc task template');
                  this.globalErrorHandlerService.handleError({ message: 'This is not a PandaDoc task template' });
                }
              },
              error: (error) => {
                this.loadingSpinnerService.hideSpinner();
                this.toastService.showToast(ToastMessageType.Error, 'Task PandaDoc failed.');
                this.globalErrorHandlerService.handleError(error);
              }
            });
        },
        error: (error) => {
          this.loadingSpinnerService.hideSpinner();
          this.toastService.showToast(ToastMessageType.Error, 'Loading Task details failed.');
          this.globalErrorHandlerService.handleError(error);
        }
      });
  }

  private loadTaskNotes(taskId: string) {
    this.taskService.getTaskNotes(taskId).subscribe({
      next: (resp) => {
        const apiNotes = resp?.body ?? [];
        this.recentTaskNote = apiNotes.find((note) => note.taskNoteType === 1)?.comments || '';
      },
      error: (err) => {
        console.error('Failed to load task notes', err);
      }
    });
  }

  public subTaskEditable(): boolean {
    return this.taskAction?.taskStatusId === 1 || this.taskAction?.taskStatusId === 4;
  }

  public onBack(): void {
    this.router.navigate(['/']).then();
  }

  public onSubmit(): void {
    if (!this.canSubmit || !this.task || !this.taskAction) {
      return;
    }

    this.taskAction.taskStatusId = 2; // Under Review

    this.loadingSpinnerService.showSpinner();
    this.taskService.updateTask(this.task).then(
      () => {
        this.loadingSpinnerService.hideSpinner();
        this.toastService.showToast(ToastMessageType.Success, 'Task updated successfully.');
        this.onBack();
      },
      (err) => {
        this.loadingSpinnerService.hideSpinner();
        this.toastService.showToast(ToastMessageType.Error, 'Error updating task status.');
        console.error('error updating task', err);
      }
    );
  }

  public openPandaDocHelper(): void {
    const documentId = this.taskAction?.pandaDocDocumentId;

    if (!this.task || !documentId) {
      this.toastService.showToast(
        ToastMessageType.Error,
        'The PandaDoc document is not available.'
      );
      return;
    }

    // Open synchronously in the click handler. Calling window.open after the
    // async session request is treated as a blocked popup by many browsers,
    // and may return null even when a tab does open.
    const popup = window.open('about:blank', '_blank');
    if (!popup) {
      this.toastService.showToast(
        ToastMessageType.Error,
        'The document could not be opened. Please allow popups for this site.'
      );
      return;
    }

    popup.opener = null;
    this.isLoadingSession = true;
    this.cdr.markForCheck();

    const submission: PandaDocCreateSessionRequestDto = {
      documentId,
      recipient: this.task.accountName,
      lifetime: 900
    };

    this.taskService
      .createPandaDocSession(submission)
      .pipe(
        takeUntil(this.unsubscribe),
        finalize(() => {
          this.isLoadingSession = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (session) => {
          if (!this.isValidSessionId(session?.id)) {
            popup.close();
            this.toastService.showToast(
              ToastMessageType.Error,
              'PandaDoc returned an invalid signing session.'
            );
            return;
          }

          popup.location.href =
            `https://app.pandadoc.com/s/${encodeURIComponent(session.id)}/`;

          if (this.subTaskEditable()) {
            this.documentOpened = true;
            this.cdr.markForCheck();
          }
        },
        error: (error) => {
          popup.close();
          this.toastService.showToast(
            ToastMessageType.Error,
            'Loading document session failed.'
          );

          this.globalErrorHandlerService.handleError(error);
        }
      });
  }

  private isValidSessionId(value: unknown): value is string {
    return (
      typeof value === 'string' &&
      /^[A-Za-z0-9_-]{1,200}$/.test(value)
    );
  }
  

  // private openPandaDoc(): void {
  //   window.open(this.pandaDocUrl, '_blank', 'noopener, noreferrer');
  //   this.isLoadingSession = false;
  // }

  /**
   * Refreshes the task data by reloading from the API
   */
  private refreshTaskData(): void {
    const taskId = this.route.snapshot.paramMap.get('id');
    const taskActionId = this.route.snapshot.paramMap.get('typeId');

    if (!taskId || !taskActionId) {
      return;
    }

    this.loadingSpinnerService.showSpinner();
    this.taskService
      .get(taskId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (task) => {
          this.task = task.body;
          if (this.task?.subTasks) {
            this.taskAction = this.task.subTasks.find(
              (subTask) => subTask.id?.toLowerCase() === taskActionId.toLowerCase()
            );
            this.taskCompleted = !this.subTaskEditable();
            if (this.taskCompleted) {
              this.documentOpened = true;
            }
          }
          this.cdr.markForCheck();
          this.loadingSpinnerService.hideSpinner();
          this.toastService.showToast(ToastMessageType.Success, 'Task data refreshed successfully.');
        },
        error: (error) => {
          this.loadingSpinnerService.hideSpinner();
          this.toastService.showToast(ToastMessageType.Error, 'Failed to refresh task data.');
          console.error('Error refreshing task:', error);
        }
      });
  }

  private handlePandaDocCompleted(): void {
    this.toastService.showToast(
      ToastMessageType.Success,
      'The document was signed successfully.'
    );

    this.pandaDocUrl = '';

    this.refreshTaskData();
    this.cdr.markForCheck();
  }
}
